import {
  CategoriesList,
  ENTITIES_CLASS,
  ENTITIES_TYPES_ORDER,
  RESOURCES_CLASS,
  RESOURCES_TYPES_ORDER,
  Trigger,
  TriggerType,
} from '../edr/incidentsGrid/types';
import { GeneralSection } from '../edr/edrIncidentDetails/types';

import { SeverityColors, SeverityScores } from '../xdr/timelineItem/types';
import { Tactic, Technique } from '../xdr/tacticsAndTechniques/types';

export function processEntitiesRowData(rowData, translateXdrEntityTypeFunction): Array<CategoriesList> {
  const { entities } = rowData;
  const categories: Array<CategoriesList> = [];

  for (const type of ENTITIES_TYPES_ORDER) {
    const filteredEntities = entities.filter(entity => {
      return entity.type === type;
    });

    filteredEntities.forEach(entity => {
      const translatedType = translateXdrEntityTypeFunction(entity.type);
      const obj: CategoriesList = {
        icon: ENTITIES_CLASS.get(entity.type),
        type: entity.type,
        text: translatedType,
        items: [],
      };
      entity.nodes.forEach(node => {
        obj.items.push(getInvolvedIncidentsForEntity(entity.type, rowData, node));
      });
      categories.push(obj);
    });
  }

  return categories;
}

export function processResourcesRowData(rowData, translateXdrResourceTypeFunction): Array<CategoriesList> {
  const { resources } = rowData;
  const categories: Array<CategoriesList> = [];

  for (const type of RESOURCES_TYPES_ORDER) {
    const filteredResources = resources.filter(resource => {
      return resource.type === type;
    });

    filteredResources.forEach(resource => {
      const translatedType = translateXdrResourceTypeFunction(resource.type);
      const obj: CategoriesList = {
        icon: RESOURCES_CLASS.get(resource.type),
        type: resource.type,
        text: translatedType,
        items: [],
      };

      resource.values.forEach((value, index) => {
        obj.items.push({
          name: value.name,
          uid: value.alertUid,
          incidents: [getIncidentData(rowData)],
          index,
        });
      });
      categories.push(obj);
    });
  }
  return categories;
}

function getInvolvedIncidentsForEntity(type, incident, entityNode) {
  const entityInvolvedInIncidents = {
    name: entityNode.name,
    uid: entityNode.uid,
    incidents: [getIncidentData(incident)],
  };
  entityNode.incidents?.forEach(element => {
    incident.correlatedIncidents?.forEach(correlatedIncident => {
      if (element._id === correlatedIncident.id) {
        entityInvolvedInIncidents.incidents.push(getIncidentData(element));
      }
    });
  });
  return entityInvolvedInIncidents;
}

export function getIncidentData(incident) {
  const { _id, number, type, isGzDemo, demoIncidentOrder } = incident;
  return {
    _id,
    number,
    type,
    isGzDemo,
    demoIncidentOrder
  };
}

export function getTriggerName(trigger: Trigger, noResultLabel = ''): string {
  let result;

  switch (trigger.type) {
    case TriggerType.FILE:
      result = trigger.specifics.fileName;
      break;
    case TriggerType.PROCESS:
      result = trigger.specifics.processName;
      if (trigger['specifics']['pid']) {
        result += '(PID:' + trigger.specifics.pid + ')';
      }
      break;
    case TriggerType.DOMAIN:
      result = trigger.specifics.url;
      break;
    default:
      result = noResultLabel;
      break;
  }

  return result;
}

export function getGeneralSectionIncidentData(incident): GeneralSection {
  const { number, created, lastUpdated, company, score, type, isGzDemo, extra } = incident;
  const { isCustomer } = extra;
  return {
    number,
    created,
    lastUpdated,
    company,
    score,
    isCustomer,
    type,
    isGzDemo,
  };
}

export function getSeverityScoreClass(incidentScore: number | null): string | undefined {
  if (!incidentScore || undefined) {
    return;
  }

  switch (true) {
    case incidentScore >= 80:
      return SeverityColors.SEVERITY_HIGH;
    case incidentScore >= 50:
      return SeverityColors.SEVERITY_MEDIUM;
    case incidentScore >= 0:
      return SeverityColors.SEVERITY_LOW;
    default:
      return '';
  }
}

export function getSeverityClass(incidentSeverity: number | null): string | undefined {
  if (!incidentSeverity || undefined) {
    return;
  }

  switch (incidentSeverity) {
    case SeverityScores.SEVERITY_HIGH:
      return SeverityColors.SEVERITY_HIGH;
    case SeverityScores.SEVERITY_MEDIUM:
      return SeverityColors.SEVERITY_MEDIUM;
    case SeverityScores.SEVERITY_LOW:
      return SeverityColors.SEVERITY_LOW;
    default:
      return '';
  }
}

export function getUniqueTechniquesAndSubtechniques(mitreTags: Array<Tactic>): Array<Technique> {
  const techniques: Array<any> = [];

  mitreTags.forEach((mitreTag: Tactic) => {
    mitreTag.techniques.forEach((technique: Technique) => {
      const alreadyAddedTechnique = techniques.find(techniqueItem => {
        return techniqueItem.id === technique.id;
      });

      // If technique is old, add new subtechniques if it does not already exist
      if (alreadyAddedTechnique) {
        const newSubtechniques = technique.subtechniques.filter(subtechnique => {
          const subtechniqueAlreadyExists = alreadyAddedTechnique.subtechniques.some(alreadyAddedSubtechnique => {
            return alreadyAddedSubtechnique.id === subtechnique.id;
          });

          if (subtechniqueAlreadyExists) {
            return false;
          }

          return true;
        });

        alreadyAddedTechnique.subtechniques.push(...newSubtechniques);

        // If technique is new, add it
      } else {
        techniques.push(technique);
      }
    });
  });

  return techniques;
}
